import { shippingOrderTitleStatusesEnum, shippingOrderTitleTypesEnum } from "helpers/enums";
import React from "react";
import { shippingOrderRegistryStatuses } from "helpers/shippingOrder";

export { getShippingOrderRegistryStatusLabel, shippingOrderRegistryStatuses } from "helpers/shippingOrder";

type ShippingOrderRegistryStatus = (typeof shippingOrderRegistryStatuses)[number]["value"];
type SignatureFilterValue = "all" | "true" | "false";

interface ShippingOrderStatusFilterValue {
    status: ShippingOrderRegistryStatus | "all";
    signatureRequired: SignatureFilterValue;
}

interface ShippingOrderStatusFilterProps {
    filtered: Record<string, { value?: ShippingOrderStatusFilterValue }>;
    column: { id: string };
    onFilteredChange: (payload: {
        id: string;
        value: ShippingOrderStatusFilterValue;
        filterType: "prepared";
        preparedFilter: {};
    }) => void;
}

const signatureRequiredFilter = {
    lastTitleType: shippingOrderTitleTypesEnum.T2,
    lastTitleStatus: shippingOrderTitleStatusesEnum.ReceivedOrCreated,
};

const buildStatusFilter = ({ status, signatureRequired }: ShippingOrderStatusFilterValue) => {
    const statusOption = shippingOrderRegistryStatuses.find(({ value }) => value === status);
    const signatureValue = signatureRequired === "all" ? null : signatureRequired === "true";

    if (statusOption) {
        if (signatureValue !== null && signatureValue !== statusOption.requiresSignature) {
            return { id: -1 };
        }
        return {
            lastTitleType: statusOption.titleType,
            lastTitleStatus: statusOption.titleStatus,
        };
    }
    if (signatureValue === true) {
        return signatureRequiredFilter;
    }
    if (signatureValue === false) {
        return { not: signatureRequiredFilter };
    }
    return {};
};

export const ShippingOrderStatusFilter = ({ filtered, column, onFilteredChange }: ShippingOrderStatusFilterProps) => {
    const value = filtered[column.id]?.value || { status: "all", signatureRequired: "all" };
    const update = (nextValue: ShippingOrderStatusFilterValue) =>
        onFilteredChange({
            id: column.id,
            value: nextValue,
            filterType: "prepared",
            preparedFilter: buildStatusFilter(nextValue),
        });

    return (
        <div>
            <select
                className="r-item__filter-select r-item__filter-select--wide field-wrap__input mb-1"
                name="shippingOrderStatus"
                aria-label="Статус ЭЗЗ"
                value={value.status}
                onChange={({ target }) =>
                    update({ ...value, status: target.value as ShippingOrderStatusFilterValue["status"] })
                }
            >
                <option value="all">Все статусы</option>
                {shippingOrderRegistryStatuses.map(({ label, value: optionValue }) => (
                    <option key={optionValue} value={optionValue}>
                        {label}
                    </option>
                ))}
            </select>
            <select
                className="r-item__filter-select r-item__filter-select--wide field-wrap__input"
                name="shippingOrderSignatureRequired"
                aria-label="Потребность в подписи"
                value={value.signatureRequired}
                onChange={({ target }) => update({ ...value, signatureRequired: target.value as SignatureFilterValue })}
            >
                <option value="all">Любая потребность в подписи</option>
                <option value="true">Требует подписания</option>
                <option value="false">Не требует подписания</option>
            </select>
        </div>
    );
};

interface ShippingOrderCreatedFilterProps {
    filtered: Record<string, { value?: string }>;
    column: { id: string };
    onFilteredChange: (payload: { id: string; value: string; filterType: "prepared"; preparedFilter: {} }) => void;
}

export const ShippingOrderCreatedFilter = ({ filtered, column, onFilteredChange }: ShippingOrderCreatedFilterProps) => {
    const value = filtered[column.id]?.value || "all";
    const update = (nextValue: string) =>
        onFilteredChange({
            id: column.id,
            value: nextValue,
            filterType: "prepared",
            preparedFilter:
                nextValue === "all"
                    ? {}
                    : nextValue === "true"
                      ? { distributionBidId: { ne: null } }
                      : { distributionBidId: null },
        });

    return (
        <select
            className="r-item__filter-select r-item__filter-select--wide field-wrap__input"
            name="shippingOrderCreated"
            aria-label="Наличие созданной заявки"
            value={value}
            onChange={({ target }) => update(target.value)}
        >
            <option value="all">Показать все</option>
            <option value="true">Заявка создана</option>
            <option value="false">Заявка не создана</option>
        </select>
    );
};
